import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../../context/AuthContext'
import { useCatalog } from '../../context/CatalogContext'
import { useOpenTickets } from '../../context/OpenTicketsContext'
import { openTicket, updateTicket } from '../../services/orders'
import { handleTableRequest, subscribeTableRequests, type TableRequest } from '../../services/tableQr'
import { DEFAULT_STATION, type OrderLine } from '../../types'
import { formatTRY } from '../../utils/money'
import { stampWaiting } from '../../utils/serving'
import { allowedHallIds, displayTable } from '../../utils/tableLabel'
import { toDayKey } from '../../utils/dayKey'
import { getDeviceCode } from '../../utils/deviceId'
import { BanknoteIcon, BellRingIcon, CheckIcon, CreditCardIcon, ReceiptTextIcon, ShoppingBagIcon, XIcon } from '../icons'
import './TableCalls.css'

/**
 * QR Sipariş, garsonun tarafı (30 Eki 2026). Müşteri masadan garson çağırınca,
 * hesap isteyince ya da sipariş verince garsonun ekranını tam ekran bir uyarı
 * kaplıyor ve zil çalıyor (kullanıcı: kasada gerekmez). Garson kırmızı
 * çarpıyla kapatırsa üst çubukta, barkod düğmesinin yanında zil simgesi
 * kalıyor; bekleyen istek kalmayınca simge kayboluyor.
 *
 * İstek, masanın salonuna bakan garsonlara düşüyor; o salona bakan kimse
 * yoksa bütün garsonlara. Biri ele alınca ötekilerin ekranından da kalkıyor.
 */

/** Tarayıcılar sesi ilk dokunuştan önce çaldırmıyor; o dokunuşta açılıyor. */
/** Yalnız yerel geliştirmede, adreste ?qrdemo: modül kapalıyken de görünür. */
export const QR_DEMO = import.meta.env.DEV && new URLSearchParams(window.location.search).has('qrdemo')

let audio: AudioContext | null = null
function unlockAudio() {
  try {
    if (!audio) {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      audio = new Ctx()
    }
    if (audio.state === 'suspended') void audio.resume()
  } catch {
    /* ses yok */
  }
}

/** Üç notalı, yükselen zil; iki kez. Mutfak zilinden ayırt edilsin. */
function ring() {
  try {
    unlockAudio()
    const ctx = audio
    if (!ctx) return
    const notes = [0, 0.75].flatMap((r) => [
      { f: 784, at: r },
      { f: 988, at: r + 0.13 },
      { f: 1319, at: r + 0.26 },
    ])
    for (const { f, at } of notes) {
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.type = 'triangle'
      o.frequency.value = f
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at)
      g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + at + 0.02)
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.35)
      o.connect(g).connect(ctx.destination)
      o.start(ctx.currentTime + at)
      o.stop(ctx.currentTime + at + 0.37)
    }
  } catch {
    /* ses yok */
  }
  navigator.vibrate?.([200, 100, 200])
}

const KIND_LABEL: Record<TableRequest['kind'], string> = {
  waiter: 'Garson çağırıyor',
  bill: 'Hesap istiyor',
  order: 'Sipariş verdi',
}

function ago(at: number, now: number) {
  const min = Math.floor((now - at) / 60_000)
  return min < 1 ? 'şimdi' : `${min} dk önce`
}

/** Aynı ürün, aynı not, aynı garson: sepetteki gibi tek satırda toplanıyor. */
function mergeLines(base: OrderLine[], add: OrderLine[]): OrderLine[] {
  const out = base.map((l) => ({ ...l }))
  for (const a of add) {
    const hit = out.find(
      (l) =>
        !l.weigh &&
        l.productId === a.productId &&
        l.unitPriceKurus === a.unitPriceKurus &&
        (l.note ?? '') === (a.note ?? '') &&
        (l.byName ?? '') === (a.byName ?? ''),
    )
    if (hit) {
      hit.qty += a.qty
      hit.lineTotalKurus = hit.qty * hit.unitPriceKurus
    } else {
      out.push(a)
    }
  }
  return out
}

export function TableCalls() {
  const { admin, tenantId, hasFeature, canOrder } = useAuth()
  const { products, categories, halls } = useCatalog()
  const { openTickets } = useOpenTickets()
  const [all, setAll] = useState<TableRequest[]>([])
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    if (!tenantId) return
    return subscribeTableRequests(tenantId, setAll)
  }, [tenantId])

  useEffect(() => {
    const i = window.setInterval(() => setNow(Date.now()), 30_000)
    const unlock = () => unlockAudio()
    window.addEventListener('pointerdown', unlock, { once: true })
    return () => {
      window.clearInterval(i)
      window.removeEventListener('pointerdown', unlock)
    }
  }, [])

  const allowed = useMemo(
    () => allowedHallIds(admin?.perms, halls),
    [admin?.perms, halls],
  )
  // Garson: kendi salonları ve hiçbir garsonun bakmadığı masalar. Kasa ve
  // patron: yalnız hiçbir garsonun bakmadığı masalar (kullanıcı, 2 Kas 2026).
  // Salonu seçilmiş kasiyer (3 Kas 2026) bunları da yalnız kendi salonunda.
  const isWaiter = admin?.role === 'waiter'
  const isCashier = admin?.role === 'cashier'
  const mine = useMemo(() => {
    const inMine = (r: (typeof all)[number]) => !allowed || (!!r.hallId && allowed.has(r.hallId))
    return all.filter((r) => (isWaiter ? !r.covered || inMine(r) : !r.covered && (!isCashier || inMine(r))))
  }, [all, allowed, isWaiter, isCashier],
  )

  // Yeni istek: uyarı açılıyor, zil çalıyor. Kapatılan uyarı, yeni bir istek
  // gelene kadar kendiliğinden açılmıyor.
  const seen = useRef<Set<string> | null>(null)
  useEffect(() => {
    const fresh = mine.filter((r) => !seen.current?.has(r.id))
    if (seen.current && fresh.length > 0) {
      setOpen(true)
      ring()
    } else if (!seen.current && mine.length > 0) {
      setOpen(true)
    }
    seen.current = new Set([...(seen.current ?? []), ...mine.map((r) => r.id)])
    if (mine.length === 0) setOpen(false)
  }, [mine])

  // Kapalıyken bekleyen varsa dakikada bir hatırlatma zili.
  useEffect(() => {
    if (open || mine.length === 0) return
    const i = window.setInterval(ring, 60_000)
    return () => window.clearInterval(i)
  }, [open, mine.length])

  const say = useCallback((text: string) => {
    setNote(text)
    window.setTimeout(() => setNote((n) => (n === text ? null : n)), 4000)
  }, [])

  const stationOf = useCallback(
    (productId: string) => {
      const p = products.find((x) => x.id === productId)
      return p?.station || categories.find((c) => c.id === p?.categoryId)?.station || DEFAULT_STATION
    },
    [products, categories],
  )

  /** Onaylanan sipariş, garsonun kendi "Masaya ekle"si gibi yazılıyor. */
  const writeOrder = (r: TableRequest) => {
    if (!tenantId || !admin) return
    const by = admin.displayName || admin.username
    const add: OrderLine[] = r.lines.map((l) => ({
      productId: l.productId,
      name: l.name,
      unitPriceKurus: l.unitPriceKurus,
      qty: l.qty,
      lineTotalKurus: l.unitPriceKurus * l.qty,
      ...(l.note ? { note: l.note } : {}),
      station: stationOf(l.productId),
      byName: by,
    }))
    const ticket = openTickets.find((t) => t.tableName === r.tableLabel)
    if (ticket) {
      const lines = stampWaiting(ticket.lines, mergeLines(ticket.lines, add))
      updateTicket(
        tenantId,
        ticket.id,
        lines,
        lines.reduce((n, l) => n + l.qty, 0),
        lines.reduce((n, l) => n + l.lineTotalKurus, 0),
      )
    } else {
      const lines = stampWaiting([], add)
      openTicket(tenantId, {
        dayKey: toDayKey(),
        deviceId: getDeviceCode(),
        cashierUid: admin.uid,
        cashierEmail: admin.email,
        tableName: r.tableLabel,
        openedByName: by,
        openedByUid: admin.uid,
        lines,
        itemCount: lines.reduce((n, l) => n + l.qty, 0),
        totalKurus: lines.reduce((n, l) => n + l.lineTotalKurus, 0),
        status: 'open',
      })
    }
  }

  const act = async (r: TableRequest, status: 'seen' | 'accepted' | 'rejected') => {
    if (busy) return
    setBusy(r.id)
    try {
      const mineNow = await handleTableRequest(r.id, status)
      if (!mineNow) {
        say(`${displayTable(r.tableLabel)}, başka biri ilgilendi`)
      } else if (status === 'accepted') {
        // Demo isteği (yalnız yerelde) gerçek adisyona yazılmıyor.
        if (!r.id.startsWith('demo-')) writeOrder(r)
        say(`${displayTable(r.tableLabel)}, sipariş mutfağa gitti`)
      }
      setAll((cur) => cur.filter((x) => x.id !== r.id))
    } catch {
      say('Gönderilemedi, internet bağlantısını kontrol edin')
    } finally {
      setBusy(null)
    }
  }

  if ((!hasFeature('qrsiparis') && !QR_DEMO) || mine.length === 0) return note ? <Toast text={note} /> : null

  return (
    <>
      <button
        type="button"
        className="bar__track bar__calls"
        onClick={() => setOpen(true)}
        aria-label={`Masa çağırıyor, ${mine.length} istek`}
        title="Masa çağırıyor"
      >
        <BellRingIcon />
        <b>{mine.length}</b>
      </button>
      {open &&
        createPortal(
          <div className="calls" role="alertdialog" aria-modal="true" aria-labelledby="calls-title">
            <header className="calls__head">
              <span className="calls__bell" aria-hidden="true">
                <BellRingIcon />
              </span>
              <div className="calls__title">
                <h2 id="calls-title">{mine.length === 1 ? 'Masa sizi çağırıyor' : `${mine.length} masa bekliyor`}</h2>
                <p>Müşteri QR menüden gönderdi</p>
              </div>
              <button type="button" className="calls__x" onClick={() => setOpen(false)} aria-label="Kapat">
                <XIcon />
              </button>
            </header>

            <ul className="calls__list">
              {mine.map((r) => {
                const ticket = openTickets.find((t) => t.tableName === r.tableLabel)
                return (
                  <li key={r.id} className={`call call--${r.kind}`}>
                    <div className="call__top">
                      <span className="call__kind">
                        {r.kind === 'order' ? <ShoppingBagIcon /> : r.kind === 'bill' ? <ReceiptTextIcon /> : <BellRingIcon />}
                        {KIND_LABEL[r.kind]}
                      </span>
                      <span className="call__ago">{ago(r.createdAt, now)}</span>
                    </div>
                    <h3 className="call__table">{displayTable(r.tableLabel)}</h3>

                    {r.kind === 'order' && (
                      <div className="call__box">
                        <ul className="call__lines">
                          {r.lines.map((l, i) => (
                            <li key={i}>
                              <b className="call__qty">{l.qty}×</b>
                              <span className="call__name">
                                {l.name}
                                {l.note && <small>{l.note}</small>}
                              </span>
                              <span className="call__price">{formatTRY(l.lineTotalKurus)}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="call__sum">
                          <span>Toplam</span>
                          <b>{formatTRY(r.totalKurus)}</b>
                        </div>
                      </div>
                    )}

                    {r.kind === 'bill' && (
                      <div className="call__box call__bill">
                        <div className="call__sum">
                          <span>Adisyon</span>
                          <b>{ticket ? formatTRY(ticket.totalKurus - (ticket.prepaidKurus ?? 0)) : 'Açık adisyon yok'}</b>
                        </div>
                        {r.payMethod && (
                          <div className="call__sum">
                            <span>Ödeme</span>
                            <span className="call__pay">
                              {r.payMethod === 'card' ? <CreditCardIcon /> : <BanknoteIcon />}
                              {r.payMethod === 'card' ? 'Kartla ödeyecek' : 'Nakit ödeyecek'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="call__actions">
                      {r.kind === 'order' ? (
                        <>
                          <button
                            type="button"
                            className="call__btn call__btn--no"
                            onClick={() => void act(r, 'rejected')}
                            disabled={busy === r.id}
                          >
                            Reddet
                          </button>
                          <button
                            type="button"
                            className="call__btn call__btn--go"
                            onClick={() => void act(r, 'accepted')}
                            disabled={busy === r.id || !canOrder}
                            title={canOrder ? undefined : 'Sipariş alma bu hesapta kapalı'}
                          >
                            <CheckIcon />
                            Onayla
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="call__btn call__btn--go"
                          onClick={() => void act(r, 'seen')}
                          disabled={busy === r.id}
                        >
                          <CheckIcon />
                          Geliyorum
                        </button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
            {note && <Toast text={note} />}
          </div>,
          document.body,
        )}
      {!open && note && <Toast text={note} />}
    </>
  )
}

function Toast({ text }: { text: string }) {
  return createPortal(
    <p className="calls__toast" role="status">
      {text}
    </p>,
    document.body,
  )
}
